// Turning training that happened somewhere else into a Khronos session (#77).
//
// The point of logging a past workout is not the record for its own sake — it
// is that the LLM plans the next session against what the body has actually
// done. So a logged workout has to arrive in exactly the shape the rest of the
// app already reads: a `workout_sessions` row, with a `plan_data` tree when
// there were exercises to name.
//
// Building a real plan tree (rather than a bespoke "manual entry" blob) is what
// makes everything else work for free — the exercise labels reach the history
// prompt, the loads reach the copied summary and the load editors, and the row
// re-runs from history like any other session. A logged run has no exercises,
// so it carries `activity` metrics instead and no tree at all.
//
// Pure and React-free so it can be unit-tested without the provider chain, and
// so an importer (a Garmin FIT export) can call the same builders the manual
// form does.

import { genStableId } from "./planEditing";
import { coerceLegacyNodeTypes } from "./planNormaliser";
import { calculateTotalDuration } from "./time";

// Activity kinds a logged session can be. `strength` is the one that carries
// exercises; the rest are described by duration/distance and are the shape
// anything imported from a watch arrives in.
export const ACTIVITY_TYPES = [
  { value: "strength", label: "Strength", tracksDistance: false },
  { value: "run", label: "Run", tracksDistance: true },
  { value: "ride", label: "Ride", tracksDistance: true },
  { value: "row", label: "Row", tracksDistance: true },
  { value: "swim", label: "Swim", tracksDistance: true },
  { value: "walk", label: "Walk / Hike", tracksDistance: true },
  { value: "class", label: "Class", tracksDistance: false },
  { value: "other", label: "Other", tracksDistance: false },
];

const ACTIVITY_BY_VALUE = new Map(ACTIVITY_TYPES.map((a) => [a.value, a]));

export const activityLabel = (value) =>
  ACTIVITY_BY_VALUE.get(value)?.label || "Workout";

export const activityTracksDistance = (value) =>
  !!ACTIVITY_BY_VALUE.get(value)?.tracksDistance;

const toPositiveNumber = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const toPositiveInt = (value) => {
  const parsed = toPositiveNumber(value);
  return parsed === null ? null : Math.round(parsed);
};

/**
 * Normalise one typed exercise row.
 *
 * Everything except the name is optional, because the honest answer to "how
 * many reps was that third set" is often "I don't remember" — and a row that
 * only names the movement still tells the planner the muscle group was worked,
 * which is the main thing the history prompt is for.
 */
export const normaliseExerciseEntry = (entry) => {
  const label = String(entry?.label || "").trim();
  if (!label) return null;
  return {
    label,
    sets: toPositiveInt(entry?.sets),
    reps: toPositiveInt(entry?.reps),
    weight: toPositiveNumber(entry?.weight),
  };
};

/**
 * Build the plan tree for a logged strength session.
 *
 * One sets-Group per exercise, holding one Work node — the same compact shape
 * docs/schema.md prescribes for identical sets, so the plan views, the copied
 * summary and the load editors all read it without special-casing. Returns null
 * when nothing usable was entered, which is the signal to store an
 * activity-only session instead.
 */
export const buildLoggedPlan = (title, exercises) => {
  const rows = (Array.isArray(exercises) ? exercises : [])
    .map(normaliseExerciseEntry)
    .filter(Boolean);
  if (rows.length === 0) return null;

  return {
    id: genStableId(),
    label: title || "Logged workout",
    type: "Strength",
    children: rows.map((row) => {
      const work = {
        id: genStableId(),
        type: "Work",
        label: row.label,
        // Reps when they were recorded. Without them the node still names the
        // movement, which is what the history prompt reads.
        unit: "reps",
        value: row.reps ?? 0,
        ...(row.weight != null ? { weight: row.weight } : {}),
      };
      return {
        id: genStableId(),
        type: "Group",
        label: row.label,
        unit: "sets",
        value: row.sets ?? 1,
        children: [work],
      };
    }),
  };
};

/**
 * Summary metrics for a session with no exercises to list.
 *
 * Returns null when nothing was measured, so the column stays NULL rather than
 * holding an object of nulls.
 */
export const buildActivitySummary = ({ distanceKm, avgHr, calories } = {}) => {
  const summary = {};
  const km = toPositiveNumber(distanceKm);
  if (km != null) summary.distance_m = Math.round(km * 1000);
  const hr = toPositiveInt(avgHr);
  if (hr != null) summary.avg_hr = hr;
  const kcal = toPositiveInt(calories);
  if (kcal != null) summary.calories = kcal;
  return Object.keys(summary).length > 0 ? summary : null;
};

/**
 * Assemble the full session payload POST /api/sessions takes.
 *
 * `startedAt` is a local datetime string from the form ("2026-09-06T18:30").
 * `ended_at` is derived from the duration rather than asked for: it is what
 * marks a session as having actually happened (see trainingHistory's
 * sessionHappened), and making the user compute it would be busywork.
 *
 * @returns {{ payload: object }|{ error: string, field?: string }}  `field`
 *   names the input to point the user at when the save is refused.
 */
export const buildLoggedSession = ({
  activityType = "strength",
  title = "",
  startedAt,
  durationMinutes,
  distanceKm,
  avgHr,
  calories,
  rating,
  notes,
  exercises,
  source = "manual",
  externalId = null,
} = {}) => {
  const minutes = toPositiveInt(durationMinutes);
  if (minutes == null) {
    return { error: "Enter how long the workout lasted.", field: "duration" };
  }

  const startMs = new Date(startedAt).getTime();
  if (!Number.isFinite(startMs)) {
    return { error: "Enter when the workout was.", field: "startedAt" };
  }
  if (startMs > Date.now() + 60 * 1000) {
    return { error: "That start time is in the future.", field: "startedAt" };
  }

  const label = String(title || "").trim() || activityLabel(activityType);
  const plan = activityType === "strength" ? buildLoggedPlan(label, exercises) : null;
  const activity = plan
    ? null
    : buildActivitySummary({ distanceKm, avgHr, calories });

  const ratingValue = toPositiveInt(rating);
  const trimmedNotes = String(notes || "").trim();

  return {
    payload: {
      started_at: new Date(startMs).toISOString(),
      ended_at: new Date(startMs + minutes * 60 * 1000).toISOString(),
      duration_minutes: minutes,
      workout_type: label,
      // A strength log carries its tree; anything else stores null rather than
      // an empty shell, so `plan_data` always means "there was a plan".
      plan_data: plan,
      ...(activity ? { activity } : {}),
      ...(ratingValue != null && ratingValue <= 10
        ? { rating: ratingValue }
        : {}),
      ...(trimmedNotes ? { notes: trimmedNotes } : {}),
      source,
      ...(externalId ? { external_id: String(externalId) } : {}),
    },
  };
};

/**
 * Turn what the LLM made of a free-text description into the same shape the
 * manual form produces (#77).
 *
 * The model is a transcriber, not an oracle: everything it returns is treated
 * as a DRAFT the user confirms. So this function's job is to make that draft
 * safe to render and edit — never to trust it. It stamps ids the model may have
 * omitted or duplicated (React keys and the engine both need them unique),
 * folds enumerated sets through the normaliser so `setScheme` forms, and clamps
 * the metadata into ranges the rest of the app already assumes.
 *
 * @returns {{ plan: object, fields: object }}  `fields` prefills the form.
 */
export const adoptParsedWorkout = (result, now = Date.now()) => {
  // Destructured defaults only cover `undefined`, and the provider hands back
  // an explicit null when it could make nothing of the text — so take the
  // argument whole and check it.
  const plan = result?.plan;
  const meta = result?.meta || {};
  if (!plan) return null;

  // Same read-path normalisation any stored plan gets: without it a model that
  // enumerated four identical sets as four sibling Work nodes leaves them
  // unfolded, and no setScheme ever forms (app/CLAUDE.md).
  const normalised = coerceLegacyNodeTypes(plan) || plan;

  // Re-stamp every id. A model asked for "unique id strings" will still hand
  // back three nodes called "1", and duplicate keys break the plan view and the
  // compiler's per-instance keying long before anyone notices the cause.
  const stamp = (node) => {
    if (!node || typeof node !== "object") return node;
    const next = { ...node, id: genStableId() };
    if (Array.isArray(next.children)) next.children = next.children.map(stamp);
    return next;
  };
  const stamped = stamp(normalised);

  const daysAgo = toPositiveInt(meta.daysAgo) ?? 0;
  const startedAt = new Date(now - Math.min(daysAgo, 365) * 86400000);

  const rating = toPositiveInt(meta.rating);
  // The model's figure first — the one the user stated or, failing that, its
  // estimate of the whole session. The plan tree's own estimate is only the
  // last resort: a transcribed workout carries no rests, so it runs well short
  // (six supersets and three sets of burpees compile to about eight minutes),
  // but a short guess the user can see and correct beats a blank field that
  // quietly blocks the save.
  const stated = toPositiveInt(meta.durationMinutes);
  const duration =
    stated ?? toPositiveInt(Math.round(calculateTotalDuration(stamped) / 60));

  return {
    plan: stamped,
    fields: {
      title: String(meta.title || stamped.label || "").trim(),
      activityType: ACTIVITY_BY_VALUE.has(meta.activityType)
        ? meta.activityType
        : "strength",
      startedAt,
      durationMinutes: duration,
      // Surfaced so the preview can say the figure was inferred rather than
      // stated — the one number the user is most likely to want to correct.
      durationEstimated: !!meta.durationEstimated || stated == null,
      rating: rating != null && rating <= 10 ? rating : null,
      notes: String(meta.notes || "").trim(),
    },
  };
};

/**
 * Assemble the session payload for an already-built plan tree — the path a
 * described workout takes, where the tree came from the LLM and the form fields
 * were only confirmed.
 *
 * @returns {{ payload: object }|{ error: string, field?: string }}  `field`
 *   names the input to point the user at when the save is refused.
 */
export const buildLoggedSessionFromPlan = ({
  plan,
  title = "",
  startedAt,
  durationMinutes,
  rating,
  notes,
  source = "manual",
  externalId = null,
} = {}) => {
  if (!plan?.children?.length) {
    return { error: "There are no exercises to save." };
  }
  const minutes = toPositiveInt(durationMinutes);
  if (minutes == null) {
    return { error: "Enter how long the workout lasted.", field: "duration" };
  }

  const startMs = new Date(startedAt).getTime();
  if (!Number.isFinite(startMs)) {
    return { error: "Enter when the workout was.", field: "startedAt" };
  }
  if (startMs > Date.now() + 60 * 1000) {
    return { error: "That start time is in the future.", field: "startedAt" };
  }

  const label = String(title || "").trim() || plan.label || "Logged workout";
  const ratingValue = toPositiveInt(rating);
  const trimmedNotes = String(notes || "").trim();

  return {
    payload: {
      started_at: new Date(startMs).toISOString(),
      ended_at: new Date(startMs + minutes * 60 * 1000).toISOString(),
      duration_minutes: minutes,
      workout_type: label,
      plan_data: { ...plan, label },
      ...(ratingValue != null && ratingValue <= 10
        ? { rating: ratingValue }
        : {}),
      ...(trimmedNotes ? { notes: trimmedNotes } : {}),
      source,
      ...(externalId ? { external_id: String(externalId) } : {}),
    },
  };
};

/** Sessions not run by the Coach — logged by hand or brought in by an importer. */
export const isLoggedSession = (session) =>
  !!session?.source && session.source !== "khronos";

/** One-line description of an activity-only session's metrics, or "". */
export const describeActivity = (activity) => {
  if (!activity || typeof activity !== "object") return "";
  const parts = [];
  const metres = Number(activity.distance_m);
  if (Number.isFinite(metres) && metres > 0) {
    parts.push(`${(metres / 1000).toFixed(metres >= 10000 ? 0 : 1)} km`);
  }
  const hr = Number(activity.avg_hr);
  if (Number.isFinite(hr) && hr > 0) parts.push(`${Math.round(hr)} bpm avg`);
  const kcal = Number(activity.calories);
  if (Number.isFinite(kcal) && kcal > 0) parts.push(`${Math.round(kcal)} kcal`);
  return parts.join(" · ");
};
